import { FirebaseAuthenticationClient } from '../firebase-authentication';

const API_BASE = process.env.REACT_APP_API_BASE ?? '/api';

type RequestOptions = {
  method?: 'GET' | 'POST' | 'PATCH' | 'DELETE';
  body?: unknown;
  signal?: AbortSignal;
};

class ApiError extends Error {
  /**
   * `reason` so existe quando a API nomeia o motivo, e hoje ela nomeia um unico:
   * pesquisa encerrada. As demais falhas de link seguem indistinguiveis de
   * proposito, para a rota publica nao virar oraculo de quais links existem.
   */
  constructor(
    readonly status: number,
    message: string,
    readonly reason?: string
  ) {
    super(message);
    this.name = 'ApiError';
  }

  /** Autenticou no Firebase mas nao tem perfil em CoreUsers. */
  get isForbidden(): boolean {
    return this.status === 403;
  }

  get isUnauthorized(): boolean {
    return this.status === 401;
  }
}

class ApiClient {
  /**
   * Em producao o Firebase Hosting reescreve /api/** para o Cloud Run e em
   * desenvolvimento o proxy do webpack aponta para o Nest local: nos dois casos
   * o front fala com a propria origem, sem CORS.
   */
  static async request<T>(path: string, options: RequestOptions = {}): Promise<T> {
    const { method = 'GET', body, signal } = options;

    const idToken = await FirebaseAuthenticationClient.getIdToken();

    if (idToken === null) {
      throw new ApiError(401, 'Sem sessao do Firebase para autenticar na API');
    }

    const response = await fetch(`${API_BASE}${path}`, {
      method,
      signal,
      headers: {
        Authorization: `Bearer ${idToken}`,
        ...(body === undefined ? {} : { 'Content-Type': 'application/json' }),
      },
      ...(body === undefined ? {} : { body: JSON.stringify(body) }),
    });

    if (!response.ok) {
      throw new ApiError(response.status, await ApiClient.extractError(response));
    }

    if (response.status === 204) return undefined as T;

    return (await response.json()) as T;
  }

  private static async extractError(response: Response): Promise<string> {
    try {
      const payload = await response.json();
      return payload?.message ?? response.statusText;
    } catch {
      return response.statusText;
    }
  }

  static get<T>(path: string, signal?: AbortSignal): Promise<T> {
    return ApiClient.request<T>(path, { method: 'GET', signal });
  }

  static post<T>(path: string, body?: unknown, signal?: AbortSignal): Promise<T> {
    return ApiClient.request<T>(path, { method: 'POST', body, signal });
  }
}

export { ApiClient, ApiError };
